/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

/* eslint-disable max-len */
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';

import { EditorContentViewComponent } from './components/editor-content-view/editor-content-view.component';
import { ExternalContentSummaryComponent } from './components/editor-content-view/editor-content/external-content-summary/external-content-summary.component';
import { MediaEmbedsComponent } from './components/editor-content-view/editor-content/media-embeds/media-embeds.component';

import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { EMBEDS_CONTENT_GENERATOR_SERVICE, EmbedsContentGeneratorService } from './services/embeds-content-generator.service';
import { IframelyEmbedsContentGeneratorService } from './services/iframely-embeds-content-generator.service';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { EditorToolbarOptionComponent } from '@app/app/editor/components/editor-toolbar/editor-toolbar-option/editor-toolbar-option.component';
import { FileUploadComponent } from '@app/app/editor/components/editor-toolbar/file-upload/file-upload.component';
import { LinkInputComponent } from '@app/app/editor/components/editor-toolbar/link-input/link-input.component';
import { FormPortfolioComponent } from '@app/app/editor/components/shared/form/form-portfolio/form-portfolio.component';
import { FormProjectComponent } from '@app/app/editor/components/shared/form/form-project/form-project.component';
import { DiagnosticQuestionsListComponent } from '@app/app/editor/components/shared/questions/diagnostic-questions-list/diagnostic-questions-list.component';
import { EditDiagnosticsQuestionComponent } from '@app/app/editor/components/shared/questions/edit-diagnostics-question/edit-diagnostics-question.component';
import { ChoiceQuestionTypeComponent } from '@app/app/editor/components/shared/questions/edit-question/choice-question-type/choice-question-type.component';
import { DragDropQuestionTypeComponent } from '@app/app/editor/components/shared/questions/edit-question/drag-drop-question-type/drag-drop-question-type.component';
import { GridQuestionTypeComponent } from '@app/app/editor/components/shared/questions/edit-question/grid-question-type/grid-question-type.component';
import { GroupQuestionTypeComponent } from '@app/app/editor/components/shared/questions/edit-question/group-question-type/group-question-type.component';
import { InfoQuestionTypeComponent } from '@app/app/editor/components/shared/questions/edit-question/info-question-type/info-question-type.component';
import { ListQuestionTypeComponent } from '@app/app/editor/components/shared/questions/edit-question/list-question-type/list-question-type.component';
import { QuizDragDropQuestionTypeComponent } from '@app/app/editor/components/shared/questions/edit-question/quiz-drag-drop-question-type/quiz-drag-drop-question-type.component';
import { QuizSliderQuestionTypeComponent } from '@app/app/editor/components/shared/questions/edit-question/quiz-slider-question-type/quiz-slider-question-type.component';
import { SliderQuestionTypeComponent } from '@app/app/editor/components/shared/questions/edit-question/slider-question-type/slider-question-type.component';
import { TextQuestionTypeComponent } from '@app/app/editor/components/shared/questions/edit-question/text-question-type/text-question-type.component';
import { QuestionsQuizListComponent } from '@app/app/editor/components/shared/questions/questions-quiz-list/questions-quiz-list.component';
import { ReviewCommentEditorComponent } from '@app/app/editor/components/shared/review-comment-editor/review-comment-editor.component';
import { CORE_RESOURCE_DATA_SERVICE } from '@app/app/page-modules/resource/services/editor/core/data.service';
import { EditorJsComponent } from '@app/app/shared/editor-js/editor-js.component';
import { RequestQueueManagerService } from '@app/app/shared/services/request-queue-manager/request-queue-manager';
import { resourceServiceFactory } from '@app/app/shared/shared-service-factory';
import { TranslocoService } from '@ngneat/transloco';
import { ColorPickerModule } from 'ngx-color-picker';
import { SendMessageDialogComponent } from '../page-modules/admin/components/members/admin-member-control-panel/send-message-dialog/send-message-dialog.component';
import { UNSPLASH_IMAGES_DATA_SERVICE } from '../page-modules/admin/services/images/unsplash-images-data.service';
import { DataFormsMainNewComponent } from '../shared/components/data-forms/data-forms-main-new/data-forms-main-new.component';
import { DataFormsMainComponent } from '../shared/components/data-forms/data-forms-main/data-forms-main.component';
import { EditCheckboxListComponent } from '../shared/components/data-forms/edit-data-form/edit-checkbox-list/edit-checkbox-list.component';
import { EditDataFormGroupsComponent } from '../shared/components/data-forms/edit-data-form/edit-data-form-groups/edit-data-form-groups.component';
import { EditDataFormMainNewComponent } from '../shared/components/data-forms/edit-data-form/edit-data-form-main-new/edit-data-form-main-new.component';
import { EditDataFormMainComponent } from '../shared/components/data-forms/edit-data-form/edit-data-form-main/edit-data-form-main.component';
import { EditDependenciesComponent } from '../shared/components/data-forms/edit-data-form/edit-dependencies/edit-dependencies.component';
import { EditFieldsNewComponent } from '../shared/components/data-forms/edit-data-form/edit-fields-new/edit-fields-new.component';
import { EditFieldsComponent } from '../shared/components/data-forms/edit-data-form/edit-fields/edit-fields.component';
import { EditGroupListNewComponent } from '../shared/components/data-forms/edit-data-form/edit-group-list-new/edit-group-list-new.component';
import { EditGroupListComponent } from '../shared/components/data-forms/edit-data-form/edit-group-list/edit-group-list.component';
import { GroupListCsvUploadDialogComponent } from '../shared/components/data-forms/edit-data-form/group-list-csv-upload-dialog/group-list-csv-upload-dialog.component';
import { BOOKING_DATA_SERVICE } from '../shared/services/booking/booking-data.service';
import { RestClientService } from '../shared/services/rest-client.service';
import * as SharedFactoryService from '../shared/shared-service-factory';
import { SharedModule } from '../shared/shared.module';
import { TranslocoRootModule } from '../transloco/transloco-root.module';
import { EditorContentComponent } from './components/editor-content-view/editor-content/editor-content.component';
import { EmbedCodeComponent } from './components/editor-content-view/editor-content/embed-code/embed-code.component';
import { MediaContentViewComponent } from './components/editor-content-view/editor-content/media-content/media-content.component';
import { MediaUploadsComponent } from './components/editor-content-view/editor-content/media-uploads/media-uploads.component';
import { TableComponent } from './components/editor-content-view/editor-content/table/table.component';
import { TinymceEditorComponent } from './components/editor-content-view/editor-content/tinymce-editor/tinymce-editor.component';
import { EditorMainComponent } from './components/editor-main/editor-main.component';
import { EditorToolbarComponent } from './components/editor-toolbar/editor-toolbar.component';
import { ChartComponent } from './components/shared/chart/chart.component';
import { CollectorFormContentComponent } from './components/shared/collector/collector-form-content/collector-form-content.component';
import { CollectorFormLogComponent } from './components/shared/collector/collector-form-log/collector-form-log.component';
import { CollectorComponent } from './components/shared/collector/collector.component';
import { FormBookingResourceItemComponent } from './components/shared/form/form-booking/form-booking-resource/form-booking-resource-item/form-booking-resource-item.component';
import { FormBookingResourceComponent } from './components/shared/form/form-booking/form-booking-resource/form-booking-resource.component';
import { FormBookingComponent } from './components/shared/form/form-booking/form-booking.component';
import { FormCheckboxComponent } from './components/shared/form/form-checkbox/form-checkbox.component';
import { FormConnectedTextboxComponent } from './components/shared/form/form-connected-textbox/form-connected-textbox.component';
import { FormFileUploadComponent } from './components/shared/form/form-file-upload/form-file-upload.component';
import { FormPrivateNoteComponent } from './components/shared/form/form-private-note/form-private-note.component';
import { FormQuestionComponent } from './components/shared/form/form-question/form-question.component';
import { FormAIAssistantSettingsComponent } from './components/shared/form/form-textbox/form-ai-assistant-settings/form-ai-assistant-settings.component';
import { FormAiSearchDialogComponent } from './components/shared/form/form-textbox/form-ai-search-dialog/form-ai-search-dialog.component';
import { FormTextboxComponent } from './components/shared/form/form-textbox/form-textbox.component';
import { FormComponent } from './components/shared/form/form.component';
import { QuestionsDiagnosticsComponent } from './components/shared/questions-diagnostics/questions-diagnostics.component';
import { EditQuizQuestionComponent } from './components/shared/questions/edit-question/edit-quiz-question.component';
import { MultipleChoiceQuestionTypeComponent } from './components/shared/questions/edit-question/multiple-choice-question-type/multiple-choice-question-type.component';
import { QuestionInstructionComponent } from './components/shared/questions/edit-question/question-instruction/question-instruction.component';
import { TrueOrFalseChoiceQuestionTypeComponent } from './components/shared/questions/edit-question/true-or-false-choice-question-type/true-or-false-choice-question-type.component';
import { QuestionsQuizComponent } from './components/shared/questions/questions-quiz.component';
import { SliderBarComponent } from './components/shared/questions/slider-bar/slider-bar.component';
import { QuizInstructionsComponent } from './components/shared/quiz/quiz-instructions/quiz-instructions.component';
import { QuizQuestionsSectionComponent } from './components/shared/quiz/quiz-questions/quiz-questions-section.component';
import { PassCriteriaCountComponent } from './components/shared/quiz/quiz-settings/pass-criteria-count/pass-criteria-count.component';
import { PassCriteriaPopupComponent } from './components/shared/quiz/quiz-settings/pass-criteria-popup/pass-criteria-popup.component';
import { QuizSettingsComponent } from './components/shared/quiz/quiz-settings/quiz-settings.component';
import { QuizComponent } from './components/shared/quiz/quiz.component';
import { SeparatorComponent } from './components/shared/separator/separator.component';
import { ApiQuizDataService } from './services/api-data.service';
import { ApiAiAssistantDataService } from './services/assistant/ai-assistant-api-data.service';
import { MockAiAssistantDataService } from './services/assistant/ai-assistant-mock-data.service';
import { AI_ASSISTANT_SERVICE, AiAssistantService } from './services/assistant/ai-assistant.service';
import { DATA_SERVICE, QuizDataService } from './services/data.service';
import { MockQuizDataService } from './services/mock-data.service';

/* eslint-enable max-len */
@NgModule({
  imports: [
    EditorModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatRippleModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatOptionModule,
    MatDialogModule,
    MatSelectModule,
    MatRadioModule,
    MatTooltipModule,
    SharedModule,
    MatCardModule,
    MatTableModule,
    DragDropModule,
    MatMenuModule,
    ColorPickerModule,
    TranslocoRootModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatAutocompleteModule,
  ],
  declarations: [
    EditorContentComponent,
    EditorToolbarComponent,
    EditorToolbarOptionComponent,
    MediaEmbedsComponent,
    EmbedCodeComponent,
    FormComponent,
    FormTextboxComponent,
    FormPrivateNoteComponent,
    FormCheckboxComponent,
    FormFileUploadComponent,
    FormBookingComponent,
    FormBookingResourceComponent,
    FormBookingResourceItemComponent,
    EditorContentComponent,
    TinymceEditorComponent,
    EditorContentViewComponent,
    EditorMainComponent,
    ReviewCommentEditorComponent,
    MediaUploadsComponent,
    MediaContentViewComponent,
    SeparatorComponent,
    ExternalContentSummaryComponent,
    LinkInputComponent,
    FileUploadComponent,
    ChartComponent,
    QuizComponent,
    CollectorComponent,
    CollectorFormContentComponent,
    CollectorFormLogComponent,
    QuizInstructionsComponent,
    QuizQuestionsSectionComponent,
    QuizSettingsComponent,
    QuestionsQuizComponent,
    QuestionsDiagnosticsComponent,
    InfoQuestionTypeComponent,
    EditQuizQuestionComponent,
    EditDiagnosticsQuestionComponent,
    SliderBarComponent,
    QuestionsQuizListComponent,
    DiagnosticQuestionsListComponent,
    QuizSliderQuestionTypeComponent,
    SliderQuestionTypeComponent,
    GridQuestionTypeComponent,
    QuestionInstructionComponent,
    QuizDragDropQuestionTypeComponent,
    DragDropQuestionTypeComponent,
    ChoiceQuestionTypeComponent,
    GroupQuestionTypeComponent,
    ListQuestionTypeComponent,
    PassCriteriaPopupComponent,
    PassCriteriaCountComponent,
    MultipleChoiceQuestionTypeComponent,
    TextQuestionTypeComponent,
    TrueOrFalseChoiceQuestionTypeComponent,
    DataFormsMainComponent,
    DataFormsMainNewComponent,
    EditDataFormMainComponent,
    EditDataFormMainNewComponent,
    EditDataFormGroupsComponent,
    EditFieldsComponent,
    EditFieldsNewComponent,
    EditDependenciesComponent,
    EditGroupListComponent,
    EditGroupListNewComponent,
    GroupListCsvUploadDialogComponent,
    EditCheckboxListComponent,
    SendMessageDialogComponent,
    EditCheckboxListComponent,
    TableComponent,
    FormConnectedTextboxComponent,
    FormQuestionComponent,
    FormAIAssistantSettingsComponent,
    FormAiSearchDialogComponent,
    EditorJsComponent,
    FormPortfolioComponent,
    FormProjectComponent,
  ],
  providers: [
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
    { provide: EMBEDS_CONTENT_GENERATOR_SERVICE, useFactory: embedsContentGeneratorServiceFactory, deps: [HttpClient] },
    { provide: DATA_SERVICE, useFactory: serviceFactory, deps: [RestClientService, TranslocoService] },
    {
      provide: AI_ASSISTANT_SERVICE,
      useFactory: aiAssistantServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: BOOKING_DATA_SERVICE,
      useFactory: SharedFactoryService.bookingServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: UNSPLASH_IMAGES_DATA_SERVICE,
      useFactory: SharedFactoryService.unsplashServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: CORE_RESOURCE_DATA_SERVICE,
      useFactory: resourceServiceFactory,
      deps: [RestClientService, TranslocoService, RequestQueueManagerService],
    },
  ],
  exports: [
    EditorMainComponent,
    QuestionsQuizComponent,
    QuestionsDiagnosticsComponent,
    EditQuizQuestionComponent,
    QuestionsQuizListComponent,
    InfoQuestionTypeComponent,
    QuizSliderQuestionTypeComponent,
    GridQuestionTypeComponent,
    QuestionInstructionComponent,
    DataFormsMainComponent,
    DataFormsMainNewComponent,
    EditorJsComponent,
    ReviewCommentEditorComponent,
    FormQuestionComponent,
    FormTextboxComponent,
    FormCheckboxComponent,
    FormConnectedTextboxComponent,
    FormFileUploadComponent,
    FormPrivateNoteComponent,
    FormPortfolioComponent,
    FormProjectComponent,
    FormBookingComponent,
    QuizComponent,
    CollectorComponent,
    EmbedCodeComponent,
  ],
})
export class PtlEditorModule {}

// -------------------------------------------------------------------------
/** Provides the embeds content generator data service. */
export function embedsContentGeneratorServiceFactory(client: HttpClient): EmbedsContentGeneratorService {
  if (environment.dataProvider === 'mock') {
    return new IframelyEmbedsContentGeneratorService(client);
  }

  return new IframelyEmbedsContentGeneratorService(client);
}

// -------------------------------------------------------------------------
/** Provides the data service. */
export function serviceFactory(client: RestClientService, translocoService: TranslocoService): QuizDataService {
  if (environment.dataProvider === 'mock') {
    return new MockQuizDataService();
  }

  return new ApiQuizDataService(client, translocoService);
}

export function aiAssistantServiceFactory(client: RestClientService, translocoService: TranslocoService): AiAssistantService {
  if (environment.dataProvider === 'mock') {
    return new MockAiAssistantDataService();
  }

  return new ApiAiAssistantDataService(client, translocoService);
}
